import { Inject, Injectable, InjectionToken, Optional } from '@angular/core';
import { BehaviorSubject } from 'rxjs';
import { wallClock, zonedDayAt, zonedTime, zoneLabel } from './graph-dates';

export { wallClock, zonedDayAt, zonedTime };

/**
 * Whose clock the 7R graphs show their times in (OE-265). By default the COMPANY's: the zone of
 * the country the organisation trades in (`settings.timeZone`), so Flash's 08:00 opening reads
 * 08:00 wherever the viewer is. The zone chip beside a graph's date switches the viewer to their
 * own device clock, and back. This is the zone of an organisation that names none (South Africa,
 * as `orgTimeZone` in @newgenus/common, which this file cannot import under jest).
 */
export const COMPANY_TIME_ZONE = 'Africa/Johannesburg';

export type GraphClockMode = 'company' | 'local';

/** The slice of Storage this needs, so a spec can hand in its own. */
export interface ClockStorage {
    getItem(key: string): string | null;
    setItem(key: string, value: string): void;
}

export const GRAPH_CLOCK_STORAGE = new InjectionToken<ClockStorage>('GRAPH_CLOCK_STORAGE');

const STORAGE_KEY = 'graphClock';

function browserStorage(): ClockStorage | undefined {
    try {
        return typeof localStorage === 'undefined' ? undefined : localStorage;
    } catch {
        return undefined;
    }
}

@Injectable({ providedIn: 'root' })
export class GraphClockService {
    private readonly storage: ClockStorage | undefined;
    private readonly modeSubject: BehaviorSubject<GraphClockMode>;
    private companyZone = COMPANY_TIME_ZONE;
    /** Emits the current mode, then every change, so open graphs repaint their times. */
    public readonly mode$;

    constructor(@Optional() @Inject(GRAPH_CLOCK_STORAGE) storage?: ClockStorage) {
        this.storage = storage || browserStorage();
        let saved: string | null = null;
        try {
            saved = this.storage?.getItem(STORAGE_KEY) ?? null;
        } catch {
            saved = null;
        }
        this.modeSubject = new BehaviorSubject<GraphClockMode>(saved === 'local' ? 'local' : 'company');
        this.mode$ = this.modeSubject.asObservable();
    }

    public get mode(): GraphClockMode {
        return this.modeSubject.value;
    }

    /** The zone times are shown in; undefined = the viewer's own clock. */
    public get timeZone(): string | undefined {
        return this.mode === 'company' ? this.companyZone : undefined;
    }

    /**
     * The organisation's zone (Paul, 1 Nov 2026: "it will not always be SAST"). Whoever loads an
     * organisation's statistics sets it; open graphs repaint when it changes.
     */
    public setCompanyTimeZone(timeZone: string | undefined): void {
        const next = timeZone || COMPANY_TIME_ZONE;
        if (next === this.companyZone) return;
        this.companyZone = next;
        this.modeSubject.next(this.mode);
    }

    /** The chip's text, e.g. "SAST". */
    public get label(): string {
        return zoneLabel(new Date(), this.timeZone);
    }

    /** False when the viewer's own clock already reads company time: nothing to switch. */
    public get canSwitch(): boolean {
        const now = new Date();
        now.setSeconds(0, 0);
        return wallClock(now, this.companyZone).getTime() !== now.getTime();
    }

    /** The chip's tooltip: whose clock this is, and what a click does. */
    public get title(): string {
        const company = this.companyZone === COMPANY_TIME_ZONE ? 'South Africa' : this.companyZone;
        if (!this.canSwitch)
            return this.companyZone === COMPANY_TIME_ZONE ? 'South African time' : `Company time (${company})`;
        const own = Intl.DateTimeFormat().resolvedOptions().timeZone;
        return this.mode === 'company'
            ? `Company time (${company}). Click for your own clock (${own}).`
            : `Your own clock (${own}). Click for company time (${company}).`;
    }

    /** A date shown on the chosen clock (display only). */
    public show(date: Date): Date;
    public show(date: Date | null | undefined): Date | null | undefined;
    public show(date: Date | null | undefined): Date | null | undefined {
        return date ? wallClock(date, this.timeZone) : date;
    }

    public toggle(): void {
        const next: GraphClockMode = this.mode === 'company' ? 'local' : 'company';
        try {
            this.storage?.setItem(STORAGE_KEY, next);
        } catch {
            // Storage blocked (private window): the choice lasts for this visit only.
        }
        this.modeSubject.next(next);
    }
}
